import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { Badge } from "@lawline/ui/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";
import { cn } from "@lawline/ui/core/cn";

import { formatDate } from "@platform/fmt";

import type { ApiKey } from "../api-keys.types";
import { type EffectiveStatus, effectiveStatus } from "../api-keys.utils";
import { KeyActions } from "./api-key-actions";

const STATUS_CLASS: Record<EffectiveStatus, string> = {
	active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
	expired: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
	revoked: "bg-muted text-muted-foreground",
};

export function ApiKeysTable({ apiKeys }: { apiKeys: ApiKey[] }) {
	const t = useTranslations("apiKeys.table");
	const tRoot = useTranslations("apiKeys");

	const columns = useMemo<ColumnDef<ApiKey>[]>(
		() => [
			{
				accessorKey: "name",
				header: t("columns.name"),
				cell: ({ row }) => <span className="font-medium">{row.original.name ?? "-"}</span>,
			},
			{
				id: "status",
				header: t("columns.status"),
				cell: ({ row }) => {
					const status = effectiveStatus(row.original);
					return (
						<Badge className={cn("border-transparent", STATUS_CLASS[status])}>
							{t(`status.${status}`)}
						</Badge>
					);
				},
			},
			{
				id: "scopes",
				header: t("columns.scopes"),
				cell: ({ row }) => (
					<div className="flex flex-wrap gap-1">
						{row.original.scopes.map((s) => (
							<Badge key={s} variant="secondary">
								{tRoot(`create.scopeOptions.${s}`)}
							</Badge>
						))}
					</div>
				),
			},
			{
				accessorKey: "createdAt",
				header: t("columns.created"),
				cell: ({ row }) => formatDate(row.original.createdAt),
			},
			{
				accessorKey: "expiresAt",
				header: t("columns.expires"),
				cell: ({ row }) => formatDate(row.original.expiresAt),
			},
			{
				accessorKey: "lastUsedAt",
				header: t("columns.lastUsed"),
				cell: ({ row }) => formatDate(row.original.lastUsedAt),
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<div className="text-right">
						<KeyActions apiKey={row.original} />
					</div>
				),
			},
		],
		[t, tRoot],
	);

	const table = useReactTable({
		data: apiKeys,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((group) => (
						<TableRow key={group.id}>
							{group.headers.map((header) => (
								<TableHead key={header.id}>
									{flexRender(header.column.columnDef.header, header.getContext())}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={columns.length}
								className="text-muted-foreground h-20 text-center text-sm"
							>
								{tRoot("emptyState")}
							</TableCell>
						</TableRow>
					) : (
						table.getRowModel().rows.map((row) => (
							<TableRow key={row.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}
